import Honeybadger from '@honeybadger-io/js'

/**
 * Reports an error to Honeybadger for monitoring and debugging purposes
 * @param error - Error to report. Can be an Error object, string, or any other value
 * @param extra - Optional additional information to include with the error report
 * @returns Promise<boolean> - Returns true if the error was successfully reported, false otherwise
 */
export const reportError = async (
  error: Error | string | unknown,
  extra?: Record<string, any>
): Promise<boolean> => {
  try {
    const errorToReport = error instanceof Error ? error : new Error(String(error))
    const message = errorToReport.message || ''
    const stack = error instanceof Error ? error.stack : extra?.stack || undefined
    const ignoredErrorPatterns = (import.meta.env.VITE_IGNORED_ERROR_PATTERNS || '')
      .split(',')
      .map((p: string) => p.trim())
      .filter(Boolean)

    // Ignore if message or stack includes any known ignored pattern
    const shouldIgnore = ignoredErrorPatterns.some(
      (pattern: string) => message.includes(pattern) || (stack && stack.includes(pattern))
    )

    if (shouldIgnore) {
      return false // Do not report
    }

    return Honeybadger.notify(errorToReport, {
      name: extra?.name || errorToReport.message,
      stack,
      context: extra?.context,
      tags: extra?.tags
    })
  } catch (_honeybadgerError) {
    return false
  }
}
